import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Grid, Cell } from 'formlabs-web-components/Grid';
import { Header } from 'formlabs-web-components/Type';
import { Button } from 'formlabs-web-components/Link';
import TextPlugin from '../../../../common/components/cms/TextPlugin';
import { analytics } from '../../../../common/utils/index';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';

import Material from './Material';
import './MaterialSelector.module.css';

const MaterialSelector = DisplayComponent(({ materials, questions }) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState([]);

  // build list of question tags for display / breadcrumb navigation
  const [breadcrumbs, setBreadcrumbs] = useState(['Start']);

  // build list of question tags setting questions
  const [questionstags, setQuestionstags] = useState(['start']);

  const filteredMaterials = materials.filter(material =>
    filters.every(v => `${material.tags}`.includes(v))
  );

  const recMaterials = filteredMaterials
    .map(material => `${material.name}`)
    .join(', ')
    .replace(/, ([^,]*)$/, ' and $1');

  const [currentQuestion, setCurrentQuestion] = useState('intro');

  const end =
    questions.filter(question => currentQuestion === `${question.question_tag}`)
      .length < 1
      ? true
      : false;

  useEffect(
    () => {
      if (end === true) {
        analytics.track('Material Selector Tool Final Step', {
          recommendation: recMaterials
        });
      }
    },
    [end]
  );

  // On tag click, build/update filter list, update active question
  const updateFilters = (tag, crumbs) => () => {
    let changeBreadcrumbs = breadcrumbs;
    let removeFilters = filters;
    let removeQuestions = questionstags;
    let updatetag = tag;

    if (tag === 'nav') {
      if (crumbs === 0) {
        setFilters([]);
        setQuestionstags(['intro']);
        updatetag = 'intro';
        setBreadcrumbs(['Start']);
      } else {
        updatetag = questionstags[crumbs - 1];

        changeBreadcrumbs.length = crumbs + 1;
        setBreadcrumbs(changeBreadcrumbs);

        removeFilters.length = crumbs - 1;
        setFilters(removeFilters);

        removeQuestions.length = crumbs;
        setQuestionstags(removeQuestions);
      }
    } else if (crumbs === 'back') {
      if (end === false) {
        changeBreadcrumbs.length = changeBreadcrumbs.length - 1;
        setBreadcrumbs(changeBreadcrumbs);
      }

      if (filters.length !== 0) {
        removeFilters.length = removeFilters.length - 1;
        setFilters(removeFilters);

        removeQuestions.length = removeQuestions.length - 1;
        setQuestionstags(removeQuestions);
      }

      updatetag = questionstags.slice(-1).pop();
    } else if (!(tag === 'start') | 'back' | 'nav') {
      let answeredQuestion = questionstags[questionstags.length - 1];

      analytics.track('Material Selector Tool Continue', {
        question: answeredQuestion,
        answer: tag
      });

      if (!breadcrumbs.includes(crumbs)) {
        changeBreadcrumbs = changeBreadcrumbs.concat(crumbs);
        setBreadcrumbs(changeBreadcrumbs);
      }
      setFilters(filters.concat(tag));

      setQuestionstags(questionstags.concat(tag));
    }

    setCurrentQuestion(updatetag);
  };

  let materialList;

  if (filters.length) {
    materialList = filteredMaterials.map(material => (
      <Material
        key={`${material.name} ${material.industryName &&
          `${material.industryName}`}`}
        {...material}
      />
    ));
  } else {
    materialList = materials.map(material => (
      <Material
        key={`${material.name} ${material.industryName &&
          `${material.industryName}`}`}
        {...material}
      />
    ));
  }

  const questionList = questions
    .filter(question => currentQuestion === `${question.question_tag}`)
    .map(question => (
      <div key={question.breadcrumb}>
        <Header level={2} displayAs={4}>
          {question.question_text}
        </Header>
        {question.helper_text && <p>{question.helper_text}</p>}
        <ul styleName="tags">
          {question.responses.map(response => (
            <li styleName="tag" key={response.tag}>
              <Button
                // if intro make blue
                color={response.tag === 'start' ? 'dark' : 'light'}
                onClick={updateFilters(
                  `${response.tag}`,
                  `${question.breadcrumb}`
                )}
              >
                {response.response_text}
              </Button>
            </li>
          ))}
        </ul>
      </div>
    ));

  return (
    <div styleName="materialSelector">
      <Grid direction="x" hasContainer={true} gutters="margin">
        <Cell smallSize={24} mediumSize={11}>
          <div styleName="sticky">
            {breadcrumbs.length > 1 && (
              <Fragment>
                <Button
                  arrow="left"
                  color="inherit-color"
                  onClick={updateFilters('back', 'back')}
                >
                  {t('Back')}
                </Button>
                <ul styleName="breadcrumbs">
                  {breadcrumbs.map((breadcrumb, index, arr) => (
                    <li key={breadcrumb}>
                      <Button
                        color="inherit-color"
                        onClick={updateFilters('nav', index)}
                        styleName={
                          arr.length - 1 === index && !end ? 'disabled' : 'blue'
                        }
                      >
                        {breadcrumb}
                      </Button>
                    </li>
                  ))}
                  {end && <li>{t('Recommendation')}</li>}
                </ul>
              </Fragment>
            )}
            {questionList}
            {end === true && (
              <div>
                <Header level={2} displayAs={4}>
                  {t('We recommend {{materials}} for your next print.', {
                    materials: recMaterials
                  })}
                </Header>
                {filteredMaterials.map(material => (
                  <TextPlugin
                    body={material.recommendation}
                    key={`desc-${material.name}${material.industryName &&
                      `${material.industryName}`}`}
                  />
                ))}

                {filteredMaterials.map(material => (
                  <Button
                    styleName="learn"
                    url={material.link}
                    arrow="right-caret"
                    color="inherit-color"
                    key={`rec-link-${material.name}${material.industryName &&
                      `${material.industryName}`}`}
                  >
                    {t('Buy {{material}} Resin Now', {
                      material: material.name
                    })}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </Cell>
        <Cell smallSize={24} mediumSize={13}>
          <ul
            styleName={classNames('materials', {
              'top-padding': breadcrumbs.length > 1
            })}
          >
            {materialList}
          </ul>
        </Cell>
      </Grid>
    </div>
  );
});

export default MaterialSelector;
